<template>
  <Dialog scrollable :common-dialog="visitDialog" :dialog-width="dialogWidth">
    <template v-slot:title>
      <div
        v-if="visitDialog"
        class="d-flex align-center justify-space-between w-100 px-2 py-1"
      >
        <div class="d-flex align-center flex-wrap gap-2">
          <h1
            class="form-title custom-nowrap-ellipsis text-capitalize mb-0 cursor-pointer font-weight-700 color-custom-blue font-size-22 mr-2"
            v-on:click="routeDetail"
          >
            Visit #{{ visitDetail.barcode }}
          </h1>

          <Priority
            v-if="detail.priority"
            :priority="detail.priority"
            class="mr-2"
          ></Priority>

          <template v-if="visitDetail.status && visitDetail.status != 4">
            <CustomStatus
              :status="visitDetail.status"
              endpoint="visit/status"
              class="mr-2"
            ></CustomStatus>
          </template>

          <v-chip
            v-if="visitDetail.status == 4"
            color="light-green lighten-5"
            class="light-green--text text--darken-3 font-weight-700 px-3"
            label
            small
          >
            <v-icon small left color="light-green darken-2"
              >mdi-check-decagram</v-icon
            >
            Completed
          </v-chip>
        </div>

        <!-- Action Buttons -->
        <div v-if="!pageLoading" class="d-flex align-center gap-2">
          <!-- Assign Team Dropdown -->
          <template v-if="visitDetail.status == 1 && engineerList.length">
            <v-menu bottom left offset-y :close-on-content-click="false">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="custom-bold-button text-white rounded-lg px-3"
                  color="cyan darken-2"
                  depressed
                  height="36"
                  :disabled="apptEngLoading"
                  :loading="apptEngLoading"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small left>mdi-account-group</v-icon>ASSIGN
                </v-btn>
              </template>
              <v-list class="pa-2 rounded-lg" min-width="340">
                <v-list-item-title
                  class="text-uppercase font-weight-700 color-custom-blue font-size-14 pb-2 border-bottom"
                >
                  Select Team
                </v-list-item-title>
                <perfect-scrollbar
                  :options="{ suppressScrollX: true }"
                  class="scroll custom-box-top-inner-shadow py-1"
                  style="max-height: 50vh; position: relative"
                >
                  <v-list-item
                    class="px-1 py-1"
                    v-for="(team, index) in engineerList"
                    :key="index"
                  >
                    <v-checkbox
                      v-model="assignedTeam"
                      :label="team.display_name"
                      :value="team.id"
                      hide-details
                      color="cyan"
                      class="mr-2 mt-0 w-100"
                      :disabled="apptEngLoading"
                      multiple
                    >
                      <template v-slot:label>
                        <div class="d-flex align-center w-100">
                          <v-avatar size="32" class="mr-2">
                            <v-img
                              :lazy-src="$defaultProfileImage"
                              :src="$assetAPIURL(team.profile_logo)"
                            ></v-img>
                          </v-avatar>
                          <div>
                            <div
                              class="font-size-14 font-weight-600 color-custom-blue text-truncate"
                            >
                              {{ team.display_name }}
                            </div>
                            <div
                              class="caption text-muted"
                              v-if="team.designation && team.designation.value"
                            >
                              {{ team.designation.value }}
                            </div>
                          </div>
                        </div>
                      </template>
                    </v-checkbox>
                  </v-list-item>
                </perfect-scrollbar>
                <v-btn
                  :disabled="apptEngLoading"
                  :loading="apptEngLoading"
                  v-on:click="updateEngineer()"
                  color="cyan darken-2 white--text"
                  class="w-100 custom-bold-button rounded-lg mt-2"
                  depressed
                  height="36"
                  >SAVE</v-btn
                >
              </v-list>
            </v-menu>
          </template>

          <!-- UnAssigned Button -->
          <v-btn
            v-if="
              isCalendarResourceRoute &&
              visitDetail &&
              visitDetail.engineers &&
              visitDetail.engineers.length &&
              visitDetail.status == 1
            "
            class="custom-bold-button rounded-lg px-3"
            color="red"
            outlined
            height="36"
            :disabled="disassignLoading"
            @click="openDisassignSelector"
          >
            <v-icon small left>mdi-account-remove</v-icon>
            UNASSIGNED
          </v-btn>

          <!-- Edit Button -->
          <v-btn
            v-if = "visitDetail.status == 1"
            class="custom-bold-button text-white rounded-lg px-4"
            color="cyan darken-1"
            depressed
            height="36"
            @click="openEditDialog"
          >
            <v-icon small left>mdi-pencil</v-icon>
            EDIT
          </v-btn>

          <!-- Duplicate Button -->
          <v-btn
            class="custom-bold-button text-white rounded-lg px-4"
            color="orange darken-1"
            depressed
            height="36"
            @click="routeToDuplicate"
          >
            <v-icon small left>mdi-checkbox-multiple-blank-outline</v-icon>
            DUPLICATE
          </v-btn>

          <!-- Close Button -->
          <v-btn
            outlined
            color="grey darken-2"
            class="custom-bold-button bg-white rounded-lg px-4"
            height="36"
            v-on:click="$emit('close', true)"
          >
            <v-icon small left>mdi-close</v-icon>
            CLOSE
          </v-btn>
        </div>
      </div>
    </template>

    <!-- Main Modal Body Content (Exact Reference Image Layout) -->
    <template v-slot:body>
      <div class="pa-4">
        <!-- Customer Title Link Banner (if present) -->
        <div v-if="customer.customer" class="mb-3">
          <router-link
            :to="
              getDefaultRoute('customer.detail', {
                params: {
                  id: customer.customer,
                },
              })
            "
            class="font-weight-700 font-size-18 color-custom-blue hover-cyan text-decoration-none"
          >
            {{ customer.display_name }}
          </router-link>
          <span
            v-if="visitDetail && visitDetail.ticket && visitDetail.ticket.title"
            class="ml-2 font-weight-600 font-size-15 text-muted"
          >
            - {{ visitDetail.ticket.title }}
          </span>
        </div>

        <!-- Top Split Card (Left White Half & Right Soft Blue Half) -->
        <v-card
          flat
          outlined
          class="rounded-lg overflow-hidden pl-4 mb-4 border-slate shadow-sm"
        >
          <v-row no-gutters>
            <!-- Left Half: Site Location & Billing Address (White Background) -->
            <v-col cols="12" md="7" class="bg-white pa-4 border-right-slate">
              <v-row>
                <!-- Site Location Column -->
                <v-col cols="12" sm="6" class="pr-sm-3 border-right-dashed-sm">
                  <div class="d-flex align-center mb-3">
                    <v-avatar size="32" color="cyan lighten-5" class="mr-2">
                      <v-icon small color="cyan darken-2"
                        >mdi-map-marker</v-icon
                      >
                    </v-avatar>
                    <span class="font-weight-700 font-size-15 color-custom-blue"
                      >Site Location</span
                    >
                  </div>

                  <a
                    v-if="property.property_address"
                    target="_blank"
                    :href="`https://maps.google.com/?q=${property.property_address}`"
                    class="font-weight-600 font-size-14 color-custom-blue text-decoration-none d-block mb-3 hover-cyan"
                  >
                    {{ property.property_address }}
                  </a>
                  <em v-else>No Site Address</em>

                  <v-divider class="my-3 border-dashed"></v-divider>

                  <div class="d-flex align-center mb-2">
                    <v-avatar size="26" color="cyan lighten-5" class="mr-2">
                      <v-icon x-small color="cyan darken-2">mdi-account</v-icon>
                    </v-avatar>
                    <span class="font-weight-700 font-size-14 text-slate-800"
                      >Contact details</span
                    >
                  </div>

                  <div
                    v-if="property_contact_person.display_name"
                    class="font-size-14 text-slate-700 font-weight-500 mb-1"
                  >
                    {{ property_contact_person.display_name }}
                  </div>
                  <div
                    v-if="property_contact_person.primary_phone"
                    class="font-size-14 text-slate-700 d-flex align-center mb-1"
                  >
                    <v-icon x-small color="cyan darken-2" class="mr-2"
                      >mdi-phone</v-icon
                    >
                    <a
                      :href="`tel:${property_contact_person.primary_phone}`"
                      class="text-slate-700 text-decoration-none"
                    >
                      {{ property_contact_person.primary_phone }}
                    </a>
                  </div>
                  <div
                    v-if="property_contact_person.primary_email"
                    class="font-size-14 text-slate-700 d-flex align-center"
                  >
                    <v-icon x-small color="cyan darken-2" class="mr-2"
                      >mdi-email-outline</v-icon
                    >
                    <a
                      :href="`mailto:${property_contact_person.primary_email}`"
                      class="text-slate-700 text-decoration-none"
                    >
                      {{ property_contact_person.primary_email }}
                    </a>
                  </div>
                </v-col>

                <!-- Billing Address Column -->
                <v-col cols="12" sm="6" class="pl-sm-3">
                  <div class="d-flex align-center mb-3">
                    <v-avatar size="32" color="green lighten-5" class="mr-2">
                      <v-icon small color="green darken-2"
                        >mdi-file-document-outline</v-icon
                      >
                    </v-avatar>
                    <span class="font-weight-700 font-size-15 color-custom-blue"
                      >Billing Address</span
                    >
                  </div>

                  <a
                    v-if="billing.property_address"
                    target="_blank"
                    :href="`https://maps.google.com/?q=${billing.property_address}`"
                    class="font-weight-600 font-size-14 color-custom-blue text-decoration-none d-block mb-3 hover-cyan"
                  >
                    {{ billing.property_address }}
                  </a>
                  <em v-else>No Billing Address</em>

                  <v-divider class="my-3 border-dashed"></v-divider>

                  <div class="d-flex align-center mb-2">
                    <v-avatar size="26" color="green lighten-5" class="mr-2">
                      <v-icon x-small color="green darken-2"
                        >mdi-account</v-icon
                      >
                    </v-avatar>
                    <span class="font-weight-700 font-size-14 text-slate-800"
                      >Contact details</span
                    >
                  </div>

                  <div
                    v-if="billing_contact_person.display_name"
                    class="font-size-14 text-slate-700 font-weight-500 mb-1"
                  >
                    {{ billing_contact_person.display_name }}
                  </div>
                  <div
                    v-if="billing_contact_person.primary_phone"
                    class="font-size-14 text-slate-700 d-flex align-center mb-1"
                  >
                    <v-icon x-small color="green darken-2" class="mr-2"
                      >mdi-phone</v-icon
                    >
                    <a
                      :href="`tel:${billing_contact_person.primary_phone}`"
                      class="text-slate-700 text-decoration-none"
                    >
                      {{ billing_contact_person.primary_phone }}
                    </a>
                  </div>
                  <div
                    v-if="billing_contact_person.primary_email"
                    class="font-size-14 text-slate-700 d-flex align-center"
                  >
                    <v-icon x-small color="green darken-2" class="mr-2"
                      >mdi-email-outline</v-icon
                    >
                    <a
                      :href="`mailto:${billing_contact_person.primary_email}`"
                      class="text-slate-700 text-decoration-none"
                    >
                      {{ billing_contact_person.primary_email }}
                    </a>
                  </div>
                </v-col>
              </v-row>
            </v-col>

            <!-- Right Half: Visit Info Overview (Soft Light Blue Background) -->
            <v-col cols="12" md="5" class="bg-blue-light pa-4">
              <!-- Top Job Type Chip -->
              <v-chip
                outlined
                color="cyan darken-2"
                class="cyan--text text--darken-3 font-weight-600 px-3 mb-4"
                label
              >
                <v-icon small left color="cyan darken-2"
                  >mdi-briefcase-outline</v-icon
                >
                {{ getScheduleType || "One-Off Job" }}
              </v-chip>

              <!-- Key Info List Table -->
              <div class="d-flex flex-column gap-3">
                <!-- Row 1: Visit Number -->
                <div class="d-flex align-center">
                  <div class="d-flex align-center" style="width: 160px">
                    <v-avatar size="28" color="blue lighten-5" class="mr-2">
                      <v-icon x-small color="primary">mdi-pound</v-icon>
                    </v-avatar>
                    <span class="text-muted font-size-14">Visit number</span>
                  </div>
                  <router-link
                    :to="{
                      name: 'admin.visit.detail',
                      params: {
                        id: visitDetail.id,
                      },
                    }"
                    target="_blank"
                    class="text-decoration-none"
                  >
                    <Barcode
                      v-if="visitDetail && visitDetail.barcode"
                      dense
                      small
                      :barcode="visitDetail.barcode"
                    ></Barcode>
                  </router-link>
                </div>

                <!-- Row 2: Appointment -->
                <div class="d-flex align-center">
                  <div class="d-flex align-center" style="width: 160px">
                    <v-avatar size="28" color="blue lighten-5" class="mr-2">
                      <v-icon x-small color="primary"
                        >mdi-calendar-blank</v-icon
                      >
                    </v-avatar>
                    <span class="text-muted font-size-14">Appointment</span>
                  </div>
                  <span class="font-weight-700 font-size-14 color-custom-blue">
                    {{ getAppointmentText || "N/A" }}
                  </span>
                </div>

                <!-- Row 3: Technicians -->
                <div class="d-flex align-center">
                  <div class="d-flex align-center" style="width: 160px">
                    <v-avatar size="28" color="blue lighten-5" class="mr-2">
                      <v-icon x-small color="primary">mdi-account-group</v-icon>
                    </v-avatar>
                    <span class="text-muted font-size-14">Technicians</span>
                  </div>
                  <div
                    v-if="visitDetail.engineers && visitDetail.engineers.length"
                    class="d-flex flex-wrap gap-1"
                  >
                    <v-chip
                      v-for="(row, index) in visitDetail.engineers"
                      :key="index"
                      color="cyan darken-1"
                      class="white--text font-weight-600 px-3"
                      small
                      label
                    >
                      <v-icon x-small left>mdi-account</v-icon>
                      {{ row.engineer && row.engineer.display_name }}
                    </v-chip>
                  </div>
                  <div
                    v-else-if="visitDetail.assigned_engineers"
                    class="d-flex flex-wrap gap-1"
                  >
                    <v-chip
                      v-for="(row, index) in visitDetail.assigned_engineers"
                      :key="index"
                      color="cyan darken-1"
                      class="white--text font-weight-600 px-3"
                      small
                      label
                    >
                      <v-icon x-small left>mdi-account</v-icon>
                      {{ row }}
                    </v-chip>
                  </div>
                  <span v-else class="text-muted font-size-14 italic"
                    >No Technicians</span
                  >
                </div>

                <!-- Row 4: Service Report PDF -->
                <div class="d-flex align-center">
                  <div class="d-flex align-center" style="width: 160px">
                    <v-avatar size="28" color="blue lighten-5" class="mr-2">
                      <v-icon x-small color="primary"
                        >mdi-file-document-outline</v-icon
                      >
                    </v-avatar>
                    <span class="text-muted font-size-14"
                      >Service Report PDF</span
                    >
                  </div>
                  <div
                    v-if="
                      visitDetail.service_form &&
                      visitDetail.service_form.length 
                    "
                    class="d-flex flex-wrap gap-1"
                  >
                    <v-btn
                      v-for="(row, index) in visitDetail.service_form"
                      :key="index"
                      class="custom-bold-button white--text"
                      color="red darken-1"
                      x-small
                      depressed
                      v-on:click.stop.prevent="downloadFile(row)"
                    >
                      <v-icon x-small left>mdi-file-pdf-box</v-icon>
                      {{ getBarcode(row) }}
                    </v-btn>
                  </div>
                  <span v-else class="text-muted font-size-14 italic"
                    >no service report pdf</span
                  >
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card>

        <!-- Bottom Notes Card (Matches Reference Design) -->
        <v-card
          flat
          outlined
          class="rounded-lg bg-white pa-4 border-slate shadow-sm"
        >
          <!-- NOTES Section -->
          <div class="d-flex align-center">
            <v-avatar size="32" color="cyan lighten-5" class="mr-2">
              <v-icon small color="cyan darken-2">mdi-notebook-outline</v-icon>
            </v-avatar>
            <span
              class="font-size-15 font-weight-700 color-custom-blue text-uppercase"
              >NOTES</span
            >
          </div>

          <v-divider class="my-4 border-slate"></v-divider>

          <!-- ADMIN NOTE Section -->
          <div>
            <div class="d-flex align-center justify-space-between mb-2">
              <div class="d-flex align-center">
                <v-avatar size="32" color="purple lighten-5" class="mr-2">
                  <v-icon small color="purple darken-2"
                    >mdi-shield-account-outline</v-icon
                  >
                </v-avatar>
                <span
                  class="font-size-15 font-weight-700 color-custom-blue text-uppercase"
                  >ADMIN NOTE</span
                >
              </div>
              <span
                v-if="
                  visitDetail.is_remark_show || (visit && visit.is_remark_show)
                "
                class="d-flex align-center font-size-13"
              >
                <v-icon color="green darken-1" small class="mr-1"
                  >mdi-check-circle</v-icon
                >
                <span class="green--text font-weight-600"
                  >Show In Application</span
                >
              </span>
            </div>
            <read-more
              class="custom-read-more font-weight-500 font-size-14 text-slate-700 pl-10"
              more-str="read more"
              :text="
                detail && detail.admin_remark
                  ? detail.admin_remark
                  : '<em class=\'text-muted\'>No admin note</em>'
              "
              link="#"
              less-str="read less"
              :max-chars="200"
            >
            </read-more>
          </div>
        </v-card>
      </div>

      <DeleteDialog :delete-dialog="cancelVisitDialog">
        <template v-slot:title>Cancel Confirmation</template>
        <template v-slot:text>
          <h3 class="font-size-16 font-weight-500 delete-text">
            Once you cancel
            <span class="font-weight-700">{{ visitDetail.barcode }}</span
            >, you won't be able to revert this later.<br />Are you sure you
            want to cancel
            <span class="font-weight-700">{{ visitDetail.barcode }}</span>
            ?
          </h3>
        </template>
        <template v-slot:action>
          <v-btn
            class="mx-2 custom-grey-border custom-bold-button"
            v-on:click="cancelVisitDialog = false"
            >No! Cancel</v-btn
          >
          <v-btn
            color="red lighten-1 white--text"
            v-on:click="cancelVisitConfirm()"
            class="mx-2 custom-grey-border custom-bold-button"
            >Yes! Cancel</v-btn
          >
        </template>
      </DeleteDialog>

      <!-- UnAssigned Engineer Confirmation Dialog -->
      <DeleteDialog :delete-dialog="disassignDialog">
        <template v-slot:title>UnAssigned Confirmation</template>
        <template v-slot:text>
          <h3 class="font-size-16 font-weight-500 delete-text">
            Are you sure you want to disassign
            <span class="font-weight-700">{{ disassignEngineerName }}</span>
            from visit
            <span class="font-weight-700">{{ visitDetail.barcode }}</span>
            ?
          </h3>
        </template>
        <template v-slot:action>
          <v-btn
            class="mx-2 custom-grey-border custom-bold-button"
            :disabled="disassignLoading"
            v-on:click="disassignDialog = false"
            >No! Cancel</v-btn
          >
          <v-btn
            color="red lighten-1 white--text"
            :disabled="disassignLoading"
            :loading="disassignLoading"
            v-on:click="disassignEngineerConfirm()"
            class="mx-2 custom-grey-border custom-bold-button"
            >Yes! UnAssigned</v-btn
          >
        </template>
      </DeleteDialog>

      <Dialog :commonDialog="disassignSelectDialog" :dialog-width="560">
        <template v-slot:title>
          <div class="d-flex align-center justify-space-between w-100">
            <div class="d-flex align-center">
              <v-icon color="red" class="mr-2">mdi-account-remove</v-icon>
              <span>Select Technician(s) to Unassign</span>
            </div>
            <v-chip
              v-if="disassignSelected.length"
              small
              color="red lighten-5"
              text-color="red darken-2"
              class="font-weight-700"
            >
              {{ disassignSelected.length }} Selected
            </v-chip>
          </div>
        </template>
        <template v-slot:body>
          <p class="text-muted font-size-14 mb-3">
            Choose the technician(s) you want to remove from this visit.
          </p>
          <div
            class="d-flex align-center justify-space-between mb-1 px-2 grey lighten-4 rounded-lg"
            style="min-height: 42px"
          >
            <span class="font-weight-600 color-custom-blue flex-grow-1 ml-2">
              Technician(s)
            </span>
            <span class="text-muted font-size-12">
              {{ visitDetail.engineers.length }} Available
            </span>
            <v-checkbox
              :input-value="allDisassignSelected"
              :indeterminate="someDisassignSelected"
              :disabled="disassignLoading"
              hide-details
              color="red"
              class="mt-0 ml-2 order-last"
              @change="toggleAllDisassign"
            ></v-checkbox>
          </div>
          <perfect-scrollbar
            :options="{ suppressScrollX: true }"
            class="scroll custom-box-top-inner-shadow"
            style="max-height: 45vh; position: relative"
          >
            <v-list class="pa-0">
            <v-list-item
              v-for="engineer in visitDetail.engineers"
              :key="engineer.id"
              class="rounded-lg px-2 mb-1"
              :class="{
                'red lighten-5': disassignSelected.includes(engineer.id),
              }"
            >
              <v-list-item-avatar>
                <v-img
                  :lazy-src="$defaultProfileImage"
                  :src="
                    $assetAPIURL(
                      engineer.engineer && engineer.engineer.profile_logo
                    )
                  "
                  class="grey lighten-5 custom-grey-border"
                ></v-img>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="font-weight-600 color-custom-blue">
                  {{
                    engineer.engineer && engineer.engineer.display_name
                      ? engineer.engineer.display_name
                      : "Unknown technician"
                  }}
                </v-list-item-title>
                <v-list-item-subtitle v-if="engineer.engineer">
                  #{{ engineer.engineer.barcode || engineer.engineer.id }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-checkbox
                v-model="disassignSelected"
                :value="engineer.id"
                :disabled="disassignLoading"
                hide-details
                color="red"
                class="mt-0 order-last"
              ></v-checkbox>
            </v-list-item>
            </v-list>
          </perfect-scrollbar>
        </template>
        <template v-slot:action>
          <v-btn
            class="mx-2 custom-grey-border custom-bold-button"
            :disabled="disassignLoading"
            v-on:click="disassignSelectDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="red lighten-1 white--text"
            :disabled="disassignLoading || !disassignSelected.length"
            :loading="disassignLoading"
            v-on:click="confirmDisassignSelection"
          >
            <v-icon small left>mdi-account-remove</v-icon>
            Continue
          </v-btn>
        </template>
      </Dialog>

      <Dialog :commonDialog="editVisitDialog">
        <template v-slot:title>Edit Visit</template>
        <template v-slot:body>
          <v-container fluid class="py-0">
            <v-form ref="editVisitFormRef" lazy-validation>
              <v-row>
                <v-col md="12" class="pb-0">
                  <div class="font-weight-600 mb-1">Visit Title</div>
                  <v-text-field
                    dense
                    filled
                    color="cyan"
                    label="Visit Title"
                    v-model.trim="editVisitForm.title"
                    solo
                    flat
                    hide-details
                  ></v-text-field>
                </v-col>
                <v-col md="6" class="pb-0">
                  <div class="font-weight-600 mb-1">Start Date</div>
                  <Datepicker
                    :defaultDate="editVisitForm.date"
                    solo
                    v-on:update:date-picker="updateEditVisitDate"
                  ></Datepicker>
                  <div class="font-weight-600 mb-1 mt-4">Start Time</div>
                  <v-autocomplete
                    dense
                    filled
                    color="cyan"
                    label="Start Time"
                    :items="timeList"
                    v-model.trim="editVisitForm.start_time"
                    solo
                    flat
                    hide-details
                  ></v-autocomplete>
                </v-col>
                <v-col md="6" class="pb-0">
                  <div class="font-weight-600 mb-1">End Date</div>
                  <Datepicker
                    :defaultDate="editVisitForm.end_date"
                    solo
                    v-on:update:date-picker="updateEditVisitEndDate"
                  ></Datepicker>
                  <div class="font-weight-600 mb-1 mt-4">End Time</div>
                  <v-autocomplete
                    dense
                    filled
                    color="cyan"
                    label="End Time"
                    :items="timeList"
                    v-model.trim="editVisitForm.end_time"
                    solo
                    flat
                    hide-details
                  ></v-autocomplete>
                </v-col>
                <v-col md="12" class="pb-0">
                  <div class="font-weight-600 mb-1">Site Location</div>
                  <v-text-field
                    dense
                    filled
                    color="cyan"
                    label="Site Location"
                    v-model.trim="editVisitForm.site_location"
                    readonly
                    solo
                    flat
                    hide-details
                  ></v-text-field>
                </v-col>
                <v-col md="12" class="pb-0">
                  <div class="font-weight-600 mb-1">Technicians</div>
                  <v-autocomplete
                    dense
                    filled
                    color="cyan"
                    label="Technicians"
                    :items="technicianOptions"
                    item-text="text"
                    item-value="value"
                    multiple
                    chips
                    v-model="editVisitForm.technicians"
                    solo
                    flat
                    hide-details
                    :loading="unassignedLoading"
                  ></v-autocomplete>
                </v-col>
                <v-col md="12" class="pb-0">
                  <div class="font-weight-600 mb-1">Admin Note</div>
                  <v-textarea
                    dense
                    filled
                    color="cyan"
                    label="Admin Note"
                    v-model.trim="editVisitForm.admin_note"
                    solo
                    flat
                    hide-details
                    rows="3"
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </template>
        <template v-slot:action>
          <v-btn
            color="cyan white--text"
            class="mx-2 custom-bold-button"
            depressed
            :loading="editVisitLoading"
            :disabled="editVisitLoading"
            @click="saveEditVisit"
          >
            Save
          </v-btn>
          <v-btn
            class="mx-2 custom-grey-border custom-bold-button"
            depressed
            :disabled="editVisitLoading"
            @click="editVisitDialog = false"
          >
            Close
          </v-btn>
        </template>
      </Dialog>

      <Dialog :commonDialog="appointmentUpdateDialog">
        <template v-slot:title>Reschedule Appointment</template>
        <template v-slot:body>
          <v-container fluid class="py-0" style="min-height: 22vh">
            <v-row>
              <v-col md="4" class="py-1 font-weight-600"> Date </v-col>
              <v-col md="4" class="py-1 font-weight-600"> Start Time </v-col>
              <v-col md="4" class="py-1 font-weight-600"> End Time </v-col>
              <v-col md="4" class="py-1">
                <Datepicker
                  :defaultDate="updateStartDate"
                  solo
                  v-on:update:date-picker="updateAppointmentDate"
                ></Datepicker>
              </v-col>
              <v-col md="4" class="py-1">
                <v-autocomplete
                  dense
                  color="cyan"
                  filled
                  id="start-time"
                  :items="timeList"
                  v-model.trim="starTimereschdule"
                  placeholder="Start Time"
                  label="Start Time"
                  solo
                  flat
                  item-color="cyan"
                  hide-details
                />
              </v-col>
              <v-col md="4" class="py-1">
                <v-autocomplete
                  dense
                  color="cyan"
                  filled
                  id="end-time"
                  :items="timeList"
                  v-model.trim="endTimereschdule"
                  placeholder="End Time"
                  label="End Time"
                  solo
                  flat
                  item-color="cyan"
                  hide-details
                />
              </v-col>
              <v-col md="12" class="py-2" v-if="false">
                <em
                  >All engineers will be removed from visit#
                  {{ visitDetail.barcode }}</em
                >
              </v-col>
            </v-row>
          </v-container>
        </template>
        <template v-slot:action>
          <v-btn
            :disabled="apptFormLoading || pageLoading"
            :loading="apptFormLoading"
            class="mx-2 custom-grey-border custom-bold-button white--text"
            color="cyan"
            v-on:click="updateAppointment"
          >
            Save
          </v-btn>
          <v-btn
            :disabled="apptFormLoading || pageLoading"
            class="mx-2 custom-grey-border custom-bold-button"
            v-on:click="appointmentUpdateDialog = false"
          >
            Close
          </v-btn>
        </template>
      </Dialog>

      <!-- Create Closer -->
      <template v-if="getPermission('closer:create') && closerDialog">
        <CreateTimeCloser
          v-if="runningTimer.id"
          is-visit-detail
          :ticket-id="ticketId"
          :visit-id="visitId"
          :timesheet-id="runningTimer.id"
          :timesheet="runningTimer.id"
          :visit-detail="visitDetail"
          :closer-dialog="closerDialog"
          :detail="detail"
          v-on:create:ticket-closer="createdTimeCloser"
          v-on:close-dialog="closerDialog = false"
        ></CreateTimeCloser>
      </template>
    </template>
  </Dialog>
</template>

<script>
import moment from "moment-timezone";
import Barcode from "@/view/pages/partials/Barcode.vue";
import Dialog from "@/view/pages/partials/Dialog.vue";
import CommonMixin from "@/core/plugins/common-mixin";
import Priority from "@/view/pages/partials/Priority.vue";
import CustomStatus from "@/view/pages/partials/CustomStatus.vue";
import {
  PUT,
  QUERY,
  POST,
  PATCH,
  GET,
} from "@/core/services/store/request.module";
import JwtService from "@/core/services/jwt.service";
import { ErrorEventBus, InitializeError } from "@/core/lib/message.lib";
import { ServiceFormEventBus } from "@/core/lib/service.form.lib";
import { toNumber, isEmpty } from "lodash";
import { downloadEvent } from "@/core/lib/ics.lib";
import DeleteDialog from "@/view/pages/partials/Delete.vue";
import Datepicker from "@/view/pages/partials/Datepicker.vue";
//import Timepicker from "@/view/pages/partials/Timepicker.vue";
// import VueTimepicker from "vue2-timepicker/src/vue-timepicker.vue";
import CreateTimeCloser from "@/view/pages/job/partials/Create-Ticket-Closer.vue";

moment.tz.setDefault(process.env.VUE_APP_TIMEZONE);

export default {
  mixins: [CommonMixin],
  props: {
    visitDialog: {
      type: Boolean,
      default: false,
    },
    isCalendar: {
      type: Boolean,
      default: false,
    },
    visit: {
      type: Number,
      default: 0,
    },
    type: {
      type: String,
      default: null,
    },
  },
  watch: {
    visit: {
      deep: true,
      immediate: true,
      handler(param) {
        if (param) {
          this.loadContent();
        }
      },
    },
  },
  created() {
    this.timeList = this.generateTimeList();
  },
  data() {
    return {
      yourFormat: "hh:mm A",
      starTimereschdule: null,
      endTimereschdule: null,
      ticketId: null,
      visitId: null,
      resumeTimerDialog: false,
      assignFormLoading: false,
      assignFormValid: true,
      appointmentUpdateDialog: false,
      editVisitDialog: false,
      editVisitLoading: false,
      editVisitForm: {
        title: null,
        date: null,
        end_date: null,
        start_time: null,
        end_time: null,
        site_location: null,
        technicians: [],
        admin_note: null,
      },
      timeList: [],
      timesheetRunningDialog: false,
      apptFormLoading: false,
      allowUpdate: false,
      timerLoading: true,
      timerDialog: false,
      closerDialog: false,
      startTimerDialog: false,
      completeDialog: false,
      updateServiceFormDialog: false,
      assignServiceFormDialog: false,
      allTimerDialog: false,
      timer_remarks: null,
      audioDataURL: null,
      timerFormValid: true,
      timerFormLoading: false,
      recordingStarted: false,
      recordingStopped: false,
      loadingEngineers: false,
      engineerDialog: false,
      apptEngLoading: false,
      runningTimer: new Object(),
      isTimmerRunning: false,
      pageLoading: false,
      detail: new Object(),
      visitDetail: {},
      customer: new Object(),
      contactPerson: new Object(),
      property: new Object(),
      property_contact_person: new Object(),
      billing_contact_person: new Object(),
      billing: new Object(),
      visitNotes: new Object(),
      particuleFieldLoading: false,
      updateDetailDialog: {
        title: false,
        instruction: false,
      },
      updateDetail: {
        title: null,
        instruction: null,
      },
      updateAppointmentFormData: {
        date: null,
        start_time: null,
        end_time: null,
      },
      serviceFormList: new Array(),
      engineerList: new Array(),
      visitEngineer: null,
      userRunningTimers: new Array(),
      timeInterval: null,
      durationInterval: null,
      timerServiceForm: new Array(),
      timeIntervalLimit: 10000,
      timerDuration: {
        hour: "00",
        minute: "00",
        second: "00",
      },
      closer: {
        pdf_url: null,
      },
      assignedTeam: [],
      dbLineItems: [],
      newLineItems: [],
      cancelVisitDialog: false,
      cancelVisitLoading: false,
      cancelVisitBarcode: null,
      lineItemLoading: false,
      disassignDialog: false,
      disassignLoading: false,
      disassignEngineerTeamId: null,
      disassignEngineerName: null,
      disassignSelectDialog: false,
      disassignSelected: [],
      unassignedEngineers: [],
      unassignedLoading: false,
    };
  },
  methods: {
    confirmDisassign(teamRow) {
      this.disassignEngineerTeamId = teamRow.id;
      this.disassignEngineerName =
        teamRow.engineer && teamRow.engineer.display_name
          ? teamRow.engineer.display_name
          : "this technician";
      this.disassignDialog = true;
    },
    openDisassignSelector() {
      if (
        !this.visitDetail ||
        !this.visitDetail.engineers ||
        !this.visitDetail.engineers.length
      )
        return;
      this.disassignSelected = [];
      // if only one engineer, directly ask for confirm on that engineer
      if (this.visitDetail.engineers.length === 1) {
        this.confirmDisassign(this.visitDetail.engineers[0]);
        return;
      }
      this.disassignSelectDialog = true;
    },
    confirmDisassignSelection() {
      if (!this.disassignSelected || !this.disassignSelected.length) return;
      // if single selected, reuse existing confirm dialog flow
      if (this.disassignSelected.length === 1) {
        const teamId = this.disassignSelected[0];
        const teamRow = this.visitDetail.engineers.find((t) => t.id === teamId);
        if (teamRow) {
          this.disassignSelectDialog = false;
          this.confirmDisassign(teamRow);
        }
        return;
      }

      this.disassignSelectDialog = false;
      this.disassignEngineerName = `${this.disassignSelected.length} technicians`;
      this.disassignDialog = true;

      /*
      // Previous flow: multiple selected -> call API without confirmation
      _this.disassignLoading = true;
      const ticketId =
        _this.visitDetail &&
        _this.visitDetail.ticket &&
        _this.visitDetail.ticket.id;
      const visitId = _this.visitDetail && _this.visitDetail.id;
      const promises = this.disassignSelected.map((id) =>
        _this.$store.dispatch(PATCH, {
          url: `job/${ticketId}/visit/${visitId}/engineer/${id}`,
        })
      );
      Promise.all(promises)
        .then(() => {
          _this.disassignSelectDialog = false;
          _this.disassignSelected = [];
          _this.loadContent();
          _this.$emit("reload", true);
          _this.$emit("reload-calendar", true);
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.disassignLoading = false;
        });
      */
    },
    toggleAllDisassign(selected) {
      this.disassignSelected = selected
        ? this.visitDetail.engineers.map((engineer) => engineer.id)
        : [];
    },
    disassignEngineerConfirm() {
      const _this = this;
      const engineerIds = _this.disassignSelected.length
        ? _this.disassignSelected
        : [_this.disassignEngineerTeamId];
      if (!engineerIds.length || !engineerIds[0]) return;
      const ticketId =
        _this.visitDetail &&
        _this.visitDetail.ticket &&
        _this.visitDetail.ticket.id;
      const visitId = _this.visitDetail && _this.visitDetail.id;
      _this.disassignLoading = true;
      Promise.all(
        engineerIds.map((id) =>
          _this.$store.dispatch(PATCH, {
            url: `job/${ticketId}/visit/${visitId}/engineer/${id}`,
          })
        )
      )
        .then(() => {
          _this.disassignDialog = false;
          _this.disassignEngineerTeamId = null;
          _this.disassignEngineerName = null;
          _this.disassignSelected = [];
          _this.loadContent();
          _this.$emit("reload", true);
          _this.$emit("reload-calendar", true);
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.disassignLoading = false;
        });
    },
    cancelVisitConfirm() {
      this.cancelVisitLoading = true;
      this.$store
        .dispatch(PUT, { url: `visit/${this.visitDetail.id}/mark-as-cancel` })
        .then(() => {
          this.loadContent();
          this.$emit("reload", true);
          this.cancelVisitDialog = false;
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.cancelVisitLoading = false;
        });
    },
    downloadFile(row, prepend_url) {
       let url = row.view_pdf_url;
      if(row?.document?.file?.url){
        url = row.document.file.url;
      }
      if (!url) {
        return false;
      }
      if (prepend_url) {
        url = `https://bthrust-fsm.s3.ap-southeast-1.amazonaws.com/${url}`;
      }
      window.open(url, "_blank");
    },
    downloadSchedule() {
      const ticketId = this.visitDetail.ticket.id;
      const token = JwtService.getToken();
      const url = `${this.$apiURL}job/${ticketId}/download-schedule?token=${token}`;
      window.open(url, "_blank");
    },
    updateEngineer() {
      const visitId = this.visitDetail.id;
      const ticketId = this.visitDetail.ticket.id;
      const _this = this;
      if (!_this.assignedTeam.length) {
        ErrorEventBus.$emit(
          "update:error",
          InitializeError("Please select an engineer then try again.")
        );
        return false;
      }
      _this.apptEngLoading = true;
      _this.$store
        .dispatch(PUT, {
          url: "job/" + ticketId + "/visit/" + visitId + "/engineer",
          data: {
            engineer: _this.assignedTeam,
          },
        })
        .then(() => {
          _this.engineerList = [];
          _this.$nextTick(() => {
            _this.loadContent();
          });
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.apptEngLoading = false;
        });
    },
    getAvailableEngineers() {
      const _this = this;
      if (!_this.visitDetail || !_this.visitDetail.started_at) return;
      _this.loadingEngineers = true;
      _this.$store
        .dispatch(QUERY, {
          url: "ticket/available-engineers",
          data: {
            start_date: moment(_this.visitDetail.started_at).format(
              "YYYY-MM-DD"
            ),
            start_time: moment(_this.visitDetail.started_at).format("HH:mm"),
            end_date: moment(_this.visitDetail.finished_at).format(
              "YYYY-MM-DD"
            ),
            end_time: moment(_this.visitDetail.finished_at).format("HH:mm"),
            force: 0,
            all: 1,
            ticket: _this.visitDetail.ticket && _this.visitDetail.ticket.id,
            visit: _this.visitDetail.id,
          },
        })
        .then(({ data }) => {
          console.log(
            "ticket/available-engineers response (getAvailableEngineers):",
            data
          );
          const available = Array.isArray(data)
            ? data
            : (data && data.data) || [];
          _this.engineerList = available;
          _this.unassignedEngineers = available;
          // _this.engineerDialog = true;
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.loadingEngineers = false;
        });
    },
    downloadPDF() {
      if (this.closer.pdf_url) {
        window.open(this.closer.pdf_url, "_blank");
      } else {
        const token = JwtService.getToken();
        const url = `${this.$apiURL}visit/${this.visitDetail.id}/closer/${this.closer.id}/download?token=${token}`;
        window.open(url, "_blank");
      }
    },
    routeToInvoice() {
      const { ticket } = this.visitDetail;

      this.$router.push(
        this.getDefaultRoute("invoice.create", {
          query: {
            customer: this.customer.id,
            property: this.property.id,
            billing: this.billing.id,
            visit: this.visitDetail.id,
            ticket: ticket.id,
          },
        })
      );
    },
    routeDetail() {
      this.$router.push(
        this.getDefaultRoute("visit.detail", {
          params: {
            id: this.visitDetail.id ?? undefined,
          },
        })
      );
    },
    closerServiceFormDialog() {
      const _this = this;
      _this.updateServiceFormDialog = false;
      _this.getActiveTimer();
      _this.loadContent();
    },
    assignServiceForm() {
      const _this = this;

      if (!_this.$refs.assignServiceForm.validate()) {
        return false;
      }

      _this.assignFormLoading = true;

      _this.$store
        .dispatch(POST, {
          url: `job/${_this.detail.id}/visit/${_this.visit}/service-form/${_this.timerServiceForm}`,
          data: {
            timesheet: _this.runningTimer.id || null,
          },
        })
        .then(() => {
          _this.timerServiceForm = new Array();
          ServiceFormEventBus.$emit("reload:service-form");
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.assignFormLoading = false;
          _this.assignServiceFormDialog = false;
          _this.getActiveTimer();
        });
    },
    getBarcode(service) {
      let barcodeStr = this.visitDetail.barcode.split("-");
      barcodeStr = barcodeStr[0];
      let perent = this.visitDetail.barcode.replace(barcodeStr, "SRV");
      return perent + "-" + service.service_barcode;
    },
    assignServiceFormDetail() {
      const _this = this;
      _this.timerLoading = true;

      _this.$store
        .dispatch(QUERY, {
          url: "job/service-forms",
          data: {
            timesheet: _this.runningTimer.id,
            customer: _this.customer.customer,
          },
        })
        .then(({ data }) => {
          _this.serviceFormList = data;
          _this.assignServiceFormDialog = true;
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.timerLoading = false;
        });
    },
    createdTimeCloser() {
      this.closerDialog = false;
      this.$emit("close", true);
      this.$emit("reload", true);
      this.getActiveTimer();
      this.loadContent();
    },
    updatedServiceForm() {
      this.updateServiceFormDialog = false;
      this.getActiveTimer();
    },
    timesheetRunningClose() {
      const _this = this;
      _this.formLoading = true;

      _this.$store
        .dispatch(PATCH, { url: "running-time-sheet" })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.formLoading = false;
          _this.getServiceFormList();
        });
    },
    checkRunningTimerStatus() {
      const _this = this;
      _this.timerLoading = true;

      _this.$store
        .dispatch(GET, { url: "running-time-sheet" })
        .then(({ data }) => {
          if (_this.lodash.isEmpty(data) === false) {
            _this.userRunningTimers = data;
            _this.timesheetRunningDialog = true;
          } else {
            _this.getServiceFormList();
          }
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.timerLoading = false;
        });
    },
    getServiceFormList() {
      const _this = this;
      _this.timerLoading = true;
      _this.$store
        .dispatch(QUERY, {
          url: "job/service-forms",
          data: {
            customer: _this.customer.customer,
          },
        })
        .then(({ data }) => {
          _this.serviceFormList = data;
          _this.startTimerDialog = true;
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.timerLoading = false;
          _this.timesheetRunningDialog = false;
        });
    },
    updateAppointmentDate(param) {
      this.updateAppointmentFormData.date = param;
    },
    openEditDialog() {
      this.unassignedEngineers = [];
      this.initEditVisitForm();
      this.fetchAvailableEngineers();
      this.editVisitDialog = true;
    },
    fetchAvailableEngineers() {
      const _this = this;
      if (!_this.visitDetail || !_this.visitDetail.started_at) return;
      const startDate = moment(_this.visitDetail.started_at).format(
        "YYYY-MM-DD"
      );
      const endDate = moment(_this.visitDetail.finished_at).format(
        "YYYY-MM-DD"
      );
      const startTime = moment(_this.visitDetail.started_at).format("HH:mm");
      const endTime = moment(_this.visitDetail.finished_at).format("HH:mm");
      const ticketId = _this.visitDetail.ticket && _this.visitDetail.ticket.id;
      const visitId = _this.visitDetail.id;
      _this.unassignedLoading = true;
      _this.$store
        .dispatch(QUERY, {
          url: "ticket/available-engineers",
          data: {
            start_date: startDate,
            end_date: endDate,
            start_time: startTime,
            end_time: endTime,
            force: 0,
            all: 1,
            ticket: ticketId,
            visit: visitId,
          },
        })
        .then(({ data }) => {
          console.log(
            "ticket/available-engineers response (fetchAvailableEngineers):",
            data
          );
          const available = Array.isArray(data)
            ? data
            : (data && data.data) || [];
          _this.unassignedEngineers = available
            .map((eng) => ({
              text:
                eng.engineer_display_name ||
                eng.display_name ||
                eng.engineer_full_name ||
                eng.engineer_name ||
                "",
              value: eng.engineer_id || eng.id || eng.engineer || null,
            }))
            .filter((eng) => eng.value !== null);
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.unassignedLoading = false;
        });
    },
    initEditVisitForm() {
      this.editVisitForm.title = this.visitDetail.title || "";
      this.editVisitForm.date = this.visitDetail.started_at
        ? moment(this.visitDetail.started_at).format("YYYY-MM-DD")
        : null;
      this.editVisitForm.end_date = this.visitDetail.finished_at
        ? moment(this.visitDetail.finished_at).format("YYYY-MM-DD")
        : null;
      this.editVisitForm.start_time = this.visitDetail.started_at
        ? moment(this.visitDetail.started_at).format("hh:mm A")
        : null;
      this.editVisitForm.end_time = this.visitDetail.finished_at
        ? moment(this.visitDetail.finished_at).format("hh:mm A")
        : null;
      this.editVisitForm.site_location = this.property.property_address || "";
      this.editVisitForm.technicians = this.technicianOptions.map(
        (item) => item.value
      );
      this.editVisitForm.admin_note =
        (this.visitDetail.ticket && this.visitDetail.ticket.admin_remark) ||
        this.visitDetail.admin_note ||
        "";
    },
    updateEditVisitDate(param) {
      this.editVisitForm.date = param;
    },
    updateEditVisitEndDate(param) {
      this.editVisitForm.end_date = param;
    },
    saveEditVisit() {
      if (!this.$refs.editVisitFormRef.validate()) {
        return false;
      }

      const ticketId = this.visitDetail.ticket && this.visitDetail.ticket.id;
      const visitId = this.visitDetail.id;
      if (!ticketId || !visitId) {
        return false;
      }

      const startTime = this.editVisitForm.start_time
        ? moment(this.editVisitForm.start_time, ["hh:mm A"]).format("HH:mm")
        : null;
      const endTime = this.editVisitForm.end_time
        ? moment(this.editVisitForm.end_time, ["hh:mm A"]).format("HH:mm")
        : null;

      const payload = {
        title: this.editVisitForm.title,
        start_date: this.editVisitForm.date,
        end_date: this.editVisitForm.end_date,
        start_time: startTime,
        end_time: endTime,
        site_location: this.editVisitForm.site_location,
        technicians: this.editVisitForm.technicians || [],
        admin_note: this.editVisitForm.admin_note,
      };

      this.editVisitLoading = true;
      this.$store
        .dispatch(PUT, {
          url: `job/${ticketId}/visit/${visitId}`,
          data: payload,
        })
        .then(() => {
          this.editVisitDialog = false;
          this.loadContent();
          this.$emit("reload", true);
          this.$emit("reload-calendar", true);
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.editVisitLoading = false;
        });
    },
    generateTimeList() {
      const list = [];
      const base = moment().startOf("day");
      for (let i = 0; i < 48; i += 1) {
        list.push(
          base
            .clone()
            .add(i * 30, "minutes")
            .format("hh:mm A")
        );
      }
      return list;
    },
    updateAppointmentStartTime(param) {
      this.updateAppointmentFormData.start_time = param;
    },
    updateAppointmentEndTime(param) {
      this.updateAppointmentFormData.end_time = param;
    },
    updateAppointment() {
      const _this = this;
      _this.apptFormLoading = true;

      _this.$store
        /* .dispatch(PATCH, {
          url:
            "job/" + _this.detail.id + "/visit/" + _this.visit + "/re-schedule",
          data: {
            date: _this.updateAppointmentFormData.date
              ? moment(_this.updateAppointmentFormData.date).format(
                  "YYYY-MM-DD"
                )
              : moment().format("YYYY-MM-DD"),
            start_time: _this.updateAppointmentFormData.start_time
              ? moment(_this.updateAppointmentFormData.start_time, [
                  "h:mm A",
                ]).format("HH:mm")
              : moment().startOf("day").format("HH:mm"),
            end_time: _this.updateAppointmentFormData.end_time
              ? moment(_this.updateAppointmentFormData.end_time, [
                  "h:mm A",
                ]).format("HH:mm")
              : moment().endOf("day").format("HH:mm"),
          },
        }) */
        .dispatch(PATCH, {
          url:
            "job/" + _this.detail.id + "/visit/" + _this.visit + "/re-schedule",
          data: {
            date: _this.updateAppointmentFormData.date
              ? moment(_this.updateAppointmentFormData.date).format(
                  "YYYY-MM-DD"
                )
              : moment().format("YYYY-MM-DD"),
            end_date: _this.updateAppointmentFormData.end_date
              ? moment(_this.updateAppointmentFormData.end_date).format(
                  "YYYY-MM-DD"
                )
              : moment().format("YYYY-MM-DD"),
            start_time: _this.starTimereschdule
              ? moment(_this.starTimereschdule, ["hh:mm a"]).format("HH:mm")
              : moment().startOf("day").format("HH:mm"),
            end_time: _this.endTimereschdule
              ? moment(_this.endTimereschdule, ["hh:mm a"]).format("HH:mm")
              : moment().endOf("day").format("HH:mm"),
          },
        })
        .then(() => {
          _this.appointmentUpdateDialog = false;
          _this.loadContent();
          _this.$emit("reload-calendar", true);
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.apptFormLoading = false;
        });
    },
    startTimer() {
      const _this = this;
      if (!_this.$refs.timerStartForm.validate()) {
        return false;
      }

      _this.timerFormLoading = true;

      _this.$store
        .dispatch(PATCH, {
          url:
            "job/" + _this.detail.id + "/visit/" + _this.visit + "/timer/start",
          data: {
            detail: _this.timer_remarks,
            service_form: _this.timerServiceForm,
          },
        })
        .then(() => {
          _this.$emit("reload", true);
          _this.startTimerDialog = false;
          _this.timerServiceForm = new Array();
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.timerFormLoading = false;
          _this.getActiveTimer();
          _this.loadContent();
        });
    },
    startTimerWithLater() {
      const _this = this;
      if (_this.isTimmerRunning) {
        _this.closerDialog = true;
      } else {
        _this.timerFormLoading = true;
        _this.$store
          .dispatch(PATCH, {
            url:
              "job/" +
              _this.detail.id +
              "/visit/" +
              _this.visit +
              "/timer/start",
          })
          .catch((error) => {
            _this.logError(error);
          })
          .finally(() => {
            _this.$emit("reload", true);
            _this.timerFormLoading = false;
            _this.getActiveTimer(true);
            _this.loadContent();
          });
      }
    },
    routeToDuplicate() {
      const {
        billing,
        customer,
        property,
        ticket,
        property_contact_person,
        billing_contact_person,
      } = this.visitDetail;

      this.$router.push(
        this.getDefaultRoute("job.create", {
          query: {
            customer: customer ? customer.id : null,
            billing: billing ? billing.id : null,
            property: property ? property.id : null,
            property_person: property_contact_person
              ? property_contact_person.id
              : null,
            billing_person: billing_contact_person
              ? billing_contact_person.id
              : null,
            duplicate: this.visitDetail ? this.visitDetail.id : null,
            ticket: ticket ? ticket.id : null,
          },
        })
      );
    },

    setTimerDuration() {
      const _this = this;
      if (_this.lodash.isEmpty(_this.runningTimer) === false) {
        let startDate = _this.lodash.toString(_this.runningTimer.date);
        if (_this.runningTimer.start_at) {
          startDate = _this.lodash.toString(
            startDate + " " + _this.runningTimer.start_at
          );
        }

        if (startDate && moment(startDate, ["YYYY-MM-DD HH:mm:ss"]).isValid()) {
          startDate = moment(startDate, ["YYYY-MM-DD HH:mm:ss"]);

          let duration = moment.duration(moment().diff(startDate));

          let hours = parseInt(duration.asHours());
          let minutes = parseInt(duration.asMinutes()) % 60;
          let seconds = parseInt(duration.asSeconds()) % 60;

          _this.timerDuration = {
            hour: hours > 0 ? _this.lodash.padStart(hours, 2, "0") : "00",
            minute: minutes > 0 ? _this.lodash.padStart(minutes, 2, "0") : "00",
            second: seconds > 0 ? _this.lodash.padStart(seconds, 2, "0") : "00",
          };
        }
      }
    },
    updateTimerDuration() {
      const _this = this;
      clearInterval(_this.durationInterval);
      _this.durationInterval = setInterval(_this.setTimerDuration, 1000);
    },
    recordingStartedUpdate(param) {
      this.recordingStarted = param;
    },
    recordingStoppedUpdate(param) {
      this.recordingStopped = param;
    },
    stopRunningTimer() {
      const _this = this;

      if (_this.recordingStarted === true && _this.recordingStopped === false) {
        ErrorEventBus.$emit(
          "update:error",
          InitializeError("Please stop the audio recorder then try again.")
        );
        return false;
      }

      _this.formLoading = true;

      _this.$store
        .dispatch(PATCH, {
          url:
            "job/" +
            _this.runningTimer.ticket +
            "/visit/" +
            _this.runningTimer.visit +
            "/timer/" +
            _this.runningTimer.id +
            "/end",
          data: { detail: _this.timer_remarks, audio: _this.audioDataURL },
        })
        .then(() => {
          _this.timerDialog = false;
          _this.timer_remarks = null;
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.formLoading = false;
          _this.getActiveTimer();
          _this.loadContent();
        });
    },
    markAsComplete() {
      const _this = this;
      _this.formLoading = true;

      _this.$store
        .dispatch(PATCH, {
          url:
            "job/" +
            _this.detail.id +
            "/visit/" +
            _this.visit +
            "/update-status/1",
        })
        .then(() => {
          _this.loadContent();
          _this.$emit("reload", true);
          _this.completeDialog = false;
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.formLoading = false;
        });
    },
    stopAllTimers() {
      const _this = this;
      _this.formLoading = true;

      _this.$store
        .dispatch(PATCH, {
          url: "visit/" + _this.visit + "/stop-all-timer",
        })
        .then(() => {
          _this.loadContent();
          _this.$emit("reload", true);
          _this.allTimerDialog = false;
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.formLoading = false;
        });
    },
    checkTimerStatus() {
      const _this = this;
      clearInterval(_this.timeInterval);
      _this.timeInterval = setInterval(
        _this.getActiveTimer,
        _this.timeIntervalLimit
      );
    },
    getActiveTimer(closer) {
      const _this = this;
      if (_this.visitDetail.status < 4) {
        _this.timerLoading = true;
        _this.$store
          .dispatch(GET, {
            url: "visit/" + _this.visit + "/running",
          })
          .then(({ data }) => {
            if (data && !isEmpty(data)) {
              _this.runningTimer = new Object(data);
              _this.ticketId = data.ticket;
              _this.visitId = data.visit;
            }

            _this.isTimmerRunning = !!data;

            if (_this.isTimmerRunning) {
              _this.resumeTimerDialog = false;
            }

            if (closer) {
              _this.$nextTick(() => {
                _this.closerDialog = true;
              });
            }
          })
          .catch((error) => {
            _this.logError(error);
          })
          .finally(() => {
            _this.timerLoading = false;
          });
      }
    },
    updateField(type) {
      const _this = this;
      if (_this.canUpdateVisit) {
        if (!_this.allowUpdate) {
          return false;
        }
        _this.onEscParticularField();
        _this.updateDetailDialog[type] = true;
        _this.updateDetail[type] = _this.visitDetail[type];
        setTimeout(function () {
          _this.$refs[type].focus();
        }, 50);
      }
    },
    onEscParticularField() {
      this.updateDetailDialog = {
        title: false,
        instruction: false,
      };
      this.updateDetail = {
        title: null,
        instruction: null,
      };
    },
    patchVisit(data) {
      const _this = this;
      return new Promise((resolve, reject) => {
        try {
          _this.$store
            .dispatch(PATCH, {
              url: "visit/" + _this.visit,
              data,
            })
            .then((response) => {
              resolve(response.data);
            })
            .catch((error) => {
              reject(error);
            });
        } catch (error) {
          reject(error);
        }
      });
    },
    updateParticularField() {
      const _this = this;
      _this.particuleFieldLoading = true;
      if (_this.canUpdateVisit) {
        _this
          .patchVisit(_this.updateDetail)
          .catch((error) => {
            _this.logError(error);
          })
          .finally(() => {
            _this.loadContent();
            _this.particuleFieldLoading = false;
            _this.onEscParticularField();
          });
      }
    },
    resumeTimer() {
      const _this = this;
      _this.timerFormLoading = true;
      _this.$store
        .dispatch(PATCH, { url: "visit/" + _this.visit + "/resume" })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.timerFormLoading = false;
          _this.getActiveTimer();
          _this.loadContent();
        });
    },
    downloadVisit() {
      try {
        let startDateArray = moment(this.visitDetail.started_at)
          .format("YYYY-MM-DD")
          .split("-")
          .map((row) => toNumber(row));

        let startTimeArray = moment(this.visitDetail.started_at)
          .format("HH:mm")
          .split(":")
          .map((row) => toNumber(row));

        let endDateArray = moment(this.visitDetail.finished_at)
          .format("YYYY-MM-DD")
          .split("-")
          .map((row) => toNumber(row));

        let endTimeArray = moment(this.visitDetail.finished_at)
          .format("HH:mm")
          .split(":")
          .map((row) => toNumber(row));

        if (startTimeArray[0] == "00") {
          startTimeArray = new Array();
        }

        const visit = {
          start: this.lodash.concat(startDateArray, startTimeArray),
          end: this.lodash.concat(endDateArray, endTimeArray),
          title: this.visitDetail && this.visitDetail.title,
          description: this.visitDetail.instruction,
          location: this.property.property_address,
          url: process.env.VUE_APP_BASE_URL,
          geo: {
            lat: toNumber(this.property.latitude),
            lon: toNumber(this.property.longitude),
          },
          categories: this.detail.ticket_type,
          status: "CONFIRMED",
          organizer: {
            name: this.detail.added_by.display_name,
            email: this.detail.added_by.user_email,
          },
          attendees: this.visitDetail.engineers.map((row) => {
            if (row.engineer) {
              return {
                name: row.engineer.full_name,
                email: row.engineer.primary_email,
              };
            }
          }),
        };
        downloadEvent(visit);
      } catch (error) {
        ErrorEventBus.$emit("update:error", InitializeError(error));
      }
    },
    getVisitScheduleType() {
      return this.detail ? this.detail.schedule_message : "";
    },
    loadContent() {
      const _this = this;
      _this.pageLoading = true;
      _this.$store
        .dispatch(GET, {
          url: "visit/" + _this.visit,
        })
        .then(({ data }) => {
          _this.visitDetail = data;

          _this.starTimereschdule = moment(_this.visitDetail.started_at).format(
            "hh:mm A"
          );
          _this.endTimereschdule = moment(_this.visitDetail.finished_at).format(
            "hh:mm A"
          );

          _this.closer = data.closer;
          _this.detail = data.ticket;
          _this.customer = data.customer;
          _this.contactPerson = data.contact_person || new Object();
          _this.property = data.property || new Object();
          _this.property_contact_person =
            data.property_contact_person || new Object();
          _this.billing_contact_person =
            data.billing_contact_person || new Object();
          _this.billing = data.billing || new Object();
          _this.visitNotes = data.notes;
          _this.allowUpdate = true;
          if (_this.visitDetail.status === 4) {
            _this.allowUpdate = false;
            clearInterval(_this.timeInterval);
            clearInterval(_this.durationInterval);
          }

          if (
            _this.visitDetail.engineers &&
            _this.visitDetail.engineers.length
          ) {
            _this.assignedTeam = _this.visitDetail.engineers.map(
              (row) => row.engineer
            );
          }
          _this.initEditVisitForm();
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.getActiveTimer();
          _this.pageLoading = false;
        });
    },
  },
  components: {
    Dialog,
    Barcode,
    Priority,
    Datepicker,
    // Timepicker,
    // VueTimepicker,
    CreateTimeCloser,
    DeleteDialog,
    CustomStatus,
  },
  computed: {
    isCalendarResourceRoute() {
      return (
        this.$route &&
        (this.$route.name === "admin.calendar-resource" ||
          (this.$route.path && this.$route.path.includes("calendar-resource")))
      );
    },
    dialogWidth() {
      return this.lodash.toSafeInteger((document.body.clientWidth / 100) * 70);
    },
    updateStartDate() {
      if (this.lodash.isEmpty(this.visitDetail) === false) {
        return moment(this.visitDetail.started_at).format("YYYY-MM-DD");
      }
      return null;
    },
    updateStartTime() {
      if (this.lodash.isEmpty(this.visitDetail) === false) {
        return moment(this.visitDetail.started_at).format("hh:mm A");
      }
      return null;
    },
    updateEndTime() {
      if (this.lodash.isEmpty(this.visitDetail) === false) {
        return moment(this.visitDetail.finished_at).format("hh:mm A");
      }
      return null;
    },
    canUpdateVisit() {
      if (this.visitDetail && this.visitDetail.status != 1) {
        return false;
      }
      return this.getPermission("visit:update");
    },
    getAppointmentTextold() {
      if (this.lodash.isEmpty(this.visitDetail) === false) {
        let date = this.formatDate(this.visitDetail.started_at);
        let startTime = moment(this.visitDetail.started_at).format("hh:mm A");
        let endTime = moment(this.visitDetail.finished_at).format("hh:mm A");
        return date + " " + startTime + " - " + endTime;
      }
      return null;
    },
    getAppointmentText() {
      if (this.lodash.isEmpty(this.visitDetail) === false) {
        let date = this.formatDate(this.visitDetail.started_at);
        let startTime = moment(this.visitDetail.started_at).format("hh:mm A");
        let enddate = this.formatDate(this.visitDetail.finished_at);
        let endTime = moment(this.visitDetail.finished_at).format("hh:mm A");
        return `${date} ${startTime} - ${enddate} ${endTime}`;
      }
      return null;
    },
    getScheduleType() {
      if (this.detail) {
        if (this.detail.type === 1) {
          return "One-Off Job";
        }
        if (this.detail.type === 2) {
          return "Recurring Job";
        }
      }
      return null;
    },
    technicianOptions() {
      // Start with already-assigned engineers
      const assigned = [];
      if (this.visitDetail && this.visitDetail.engineers) {
        this.visitDetail.engineers.forEach((row) => {
          const engineer = row.engineer || {};
          assigned.push({
            text: engineer.display_name || row.display_name || "",
            value: engineer.id || row.id || engineer.value || "",
          });
        });
      }
      // Merge with unassigned engineers from the API (avoid duplicates)
      const assignedIds = new Set(assigned.map((e) => String(e.value)));
      const extra = (this.unassignedEngineers || []).filter(
        (e) => !assignedIds.has(String(e.value))
      );
      return [...assigned, ...extra];
    },
    allDisassignSelected() {
      return (
        this.visitDetail.engineers &&
        this.visitDetail.engineers.length > 0 &&
        this.disassignSelected.length === this.visitDetail.engineers.length


      );
    },
    someDisassignSelected() {
      return (
        this.disassignSelected.length > 0 && !this.allDisassignSelected
      );
    },
  },
};
</script>

<style scoped>
.border-slate {
  border-color: #e2e8f0 !important;
}
.border-right-slate {
  border-right: 1px solid #e2e8f0 !important;
}
.border-right-dashed-sm {
  border-right: 1px dashed #cbd5e1 !important;
}
@media (max-width: 600px) {
  .border-right-dashed-sm {
    border-right: none !important;
    border-bottom: 1px dashed #cbd5e1 !important;
    padding-bottom: 12px;
    margin-bottom: 12px;
  }
}
.border-dashed {
  border-style: dashed !important;
}
.bg-slate-50 {
  background-color: #f8fafc !important;
}
.bg-blue-light {
  background-color: #eef6fc !important;
}
.hover-cyan:hover {
  color: #00b4d8 !important;
}
.gap-1 {
  gap: 4px;
}
.gap-2 {
  gap: 8px;
}
.gap-3 {
  gap: 12px;
}
</style>
